import { useCallback, useEffect, useRef } from 'react';
import { findConnectedNodes } from './utils/graphTraversal';
import { TIDY_SAVE_DELAY_MS } from './positionPersistence/constants';
import { LAYOUT_ANIMATION_DURATION } from './flowchartLayout/constants';
import { centerCanvasOnConnectTarget, startWatchingConnectTarget } from './flowchartLayout/centerAfterConnect';
import { OPTIMISTIC_CAMPAIGN_CARD } from '../../../../hooks/campaignNesting/optimisticCampaignCard';

/** Wait for tidy persist + FLIP before recording the combined connection undo entry. */
const CONNECTION_COMBINED_UNDO_DELAY_MS = TIDY_SAVE_DELAY_MS + LAYOUT_ANIMATION_DURATION + 80;

/**
 * useConnectionHandler Hook
 *
 * Handles connection completion events and triggers auto-tidy on both chains
 * when a new connection is made between nodes.
 */
export function useConnectionHandler({
  tasks,
  autoArrange = false,
  isMultiCampaignMode = false,
  clearCustomPositionsForNodeIds = null,
  setPanAfterConnectRef = null,
  connectLayoutCenterRef = null,
  pendingCenterTaskIdRef: pendingCenterTaskIdRefProp = null,
  beginLayoutHold = null,
}) {
  // Ref to hold the auto-layout handler (set externally)
  // This allows us to call it from onConnectionComplete without circular dependency
  const handleAutoLayoutRef = useRef(null);
  /** Latest tasks for the deferred tidy (avoids stale graph when timeout runs). */
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  const cancelCenterWatchRef = useRef(null);
  const pendingCenterTaskIdRef = pendingCenterTaskIdRefProp || { current: null };

  const scheduleCombinedUndoRecord = useCallback((undoDefer, originalPositions) => {
    const recordCombined = undoDefer?.recordCombinedAction;
    if (!recordCombined) return;

    setTimeout(() => {
      recordCombined(originalPositions, tasksRef.current);
    }, CONNECTION_COMBINED_UNDO_DELAY_MS);
  }, []);

  const panToConnectTarget = useCallback((taskId, position, durationMs) => {
    if (!taskId) return false;
    pendingCenterTaskIdRef.current = null;
    cancelCenterWatchRef.current?.();
    cancelCenterWatchRef.current = null;
    return centerCanvasOnConnectTarget(
      taskId,
      position,
      durationMs,
      setPanAfterConnectRef?.current,
    );
  }, [setPanAfterConnectRef]);

  if (connectLayoutCenterRef) {
    connectLayoutCenterRef.current = (positions, durationMs) => {
      const taskId = pendingCenterTaskIdRef.current;
      if (!taskId || !positions?.[taskId]) return false;
      return panToConnectTarget(taskId, positions[taskId], durationMs);
    };
  }

  const followConnectTarget = useCallback((targetId) => {
    pendingCenterTaskIdRef.current = targetId;
    cancelCenterWatchRef.current?.();
    // Wait for the explicit tidy destination (connect or add-note). Starting a
    // watcher immediately can pan to an implicit ELK dest, then restart.
    const timeoutId = setTimeout(() => {
      if (pendingCenterTaskIdRef.current !== targetId) return;
      cancelCenterWatchRef.current = startWatchingConnectTarget({
        taskId: targetId,
        onCenter: (position, durationMs) => {
          if (pendingCenterTaskIdRef.current !== targetId) return;
          panToConnectTarget(targetId, position, durationMs);
        },
      });
    }, 420);
    cancelCenterWatchRef.current = () => clearTimeout(timeoutId);
  }, [panToConnectTarget]);

  // Same tidy as a new connection. Hold first so the implicit dependency pass
  // cannot commit the pre-delete coordinates over the animated layout.
  const scheduleGraphTidy = useCallback(() => {
    beginLayoutHold?.();
    setTimeout(() => {
      handleAutoLayoutRef.current?.(null, { skipRecordAction: true });
    }, 50);
  }, [beginLayoutHold]);

  useEffect(() => {
    const tidy = () => scheduleGraphTidy();
    const centerNewCampaign = (event) => {
      const taskId = event.detail?.taskId;
      if (taskId) followConnectTarget(taskId);
    };
    window.addEventListener('flowchartTidyGraph', tidy);
    window.addEventListener(OPTIMISTIC_CAMPAIGN_CARD, centerNewCampaign);
    return () => {
      window.removeEventListener('flowchartTidyGraph', tidy);
      window.removeEventListener(OPTIMISTIC_CAMPAIGN_CARD, centerNewCampaign);
    };
  }, [scheduleGraphTidy, followConnectTarget]);

  // Handler for when a connection is completed - triggers auto-tidy on both chains
  const handleConnectionComplete = useCallback((sourceId, targetId, undoBundle = null) => {
    const { undoDefer, originalPositions } = undoBundle || {};

    beginLayoutHold?.();
    followConnectTarget(targetId);

    setTimeout(() => {
      const latestTasks = tasksRef.current;
      const sourceConnected = findConnectedNodes(new Set([sourceId]), latestTasks);
      const targetConnected = findConnectedNodes(new Set([targetId]), latestTasks);
      const allConnected = new Set([...sourceConnected, ...targetConnected]);

      clearCustomPositionsForNodeIds?.(allConnected);

      // Full animated ELK tidy (same path as the Tidy button). Passing the
      // connected subset uses the legacy packer and snaps nodes into place.
      if (handleAutoLayoutRef.current) {
        handleAutoLayoutRef.current(null, { skipRecordAction: true });
      }

      scheduleCombinedUndoRecord(undoDefer, originalPositions);
    }, 50);
  }, [clearCustomPositionsForNodeIds, scheduleCombinedUndoRecord, followConnectTarget, beginLayoutHold]);

  // Setter for the auto-layout handler ref
  const setAutoLayoutHandler = useCallback((handler) => {
    handleAutoLayoutRef.current = handler;
  }, []);

  return {
    handleConnectionComplete,
    scheduleGraphTidy,
    setAutoLayoutHandler,
    followTaskAfterLayout: followConnectTarget,
  };
}

export default useConnectionHandler;
